import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "../../../Toaster/toast";
import {
  PrismBanner,
  PrismButton,
  PrismDialog,
  PrismInputFile,
  PrismInputText,
  PrismInputTextarea,
  PrismRadio,
  PrismRadioGroup,
  PrismSelect,
  PrismTypeahead,
} from "@prism-ui/react";
import MessageEditor from "../../../MessageEditor/MessageEditor.tsx";
import Home from "../../../Home/Home.jsx";
import { normalizeSectionOrder } from "../../../Home/sectionOrder";
import CharCounter from "./CharCounter";
import "./HomeContent.css";
import AdminPane from '../../AdminPane';

/* eslint-disable @typescript-eslint/no-explicit-any */

type Section = "hero" | "mission" | "sessions" | "projects" | "leaders" | "joinUs";

const CTA_STATES = [
  {
    value: "apply",
    label: "Apply Now (application window open)",
    editorLabel: "“Apply Now” message",
    help: "Shown on the public page while an application window is open.",
  },
  {
    value: "infoNotif",
    label: "Info Sessions (window not yet open)",
    editorLabel: "“Info Sessions” message",
    help: "Shown before the next application window opens.",
  },
  {
    value: "fairNotif",
    label: "Project Fair (window closed)",
    editorLabel: "“Project Fair” message",
    help: "Shown after all application windows have closed.",
  },
];

/**
 * Resolve an approved project's `logo` column to an absolute URL the home page can render.
 * Mirrors the canonical logic in ProjectCard/ProjectFairPrint: legacy values are a bare
 * filename that lives under the project-fair logos folder; newer values already carry an
 * `api/files/...` path (which only needs a leading slash). `GENERIC`/empty has no usable
 * project image, so the tile falls back to its existing image (or the "No image" placeholder).
 */
function resolveProjectLogo(project: any): string {
  const logo = project?.logo;
  if (!logo || logo === "GENERIC") return "";
  if (logo.indexOf("api/files") < 0) {
    return `/api/files/images/project-fair/${project.year}/logos/${logo}`;
  }
  return logo.startsWith("/") ? logo : `/${logo}`;
}

/**
 * Deterministic JSON stringify with recursively sorted object keys, so two structurally
 * equal objects compare equal regardless of key insertion order. Used to detect whether a
 * draft section differs from the published one (a freshly-edited draft object can carry the
 * same data in a different key order than the DB-loaded published document).
 */
const stableStringify = (value: any): string => {
  if (value === null || typeof value !== "object") return JSON.stringify(value ?? null);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  const keys = Object.keys(value).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`).join(",")}}`;
};

const summarizeCount = (count: number, noun: string): string =>
  `${count} ${noun}${count === 1 ? "" : "s"}`;

/**
 * Map live `home_leaders` rows to the denormalized `leaders.items` render-snapshot shape
 * (active only, ordered by display_order). Shared by the immediate-CRUD resync and the
 * Cancel-restore path so both produce an identical snapshot.
 */
const leaderSnapshotItems = (list: any[]): any[] =>
  list
    .filter((l) => l.active)
    .sort((a, b) => a.display_order - b.display_order)
    .map((l) => ({
      leaderId: l.id,
      name: l.name,
      title: l.title,
      quote: l.quote,
      headshotKey: l.headshot_key,
      order: l.display_order,
    }));

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const HomeContent: React.FC<any> = ({ currentUser }) => {
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [doc, setDoc] = useState<any>(null);
  // Baseline of the last draft persisted to the server. Reorder arrows (and any other
  // pane-level edit) mutate `doc` in memory only; comparing against this tells us whether
  // there are unsaved changes so we can surface the Save affordance and warn before leaving.
  const [savedDoc, setSavedDoc] = useState<any>(null);
  const [publishedDoc, setPublishedDoc] = useState<any>(null);
  const [version, setVersion] = useState<number>(0);
  const [leaders, setLeaders] = useState<any[]>([]);
  const [projectOptions, setProjectOptions] = useState<any[]>([]);
  const [captionLimit, setCaptionLimit] = useState(200);
  const [quoteLimit, setQuoteLimit] = useState(500);
  const [activeSection, setActiveSection] = useState<Section | null>(null);
  const [sectionSnapshot, setSectionSnapshot] = useState<any>(null);
  const [preview, setPreview] = useState(false);
  const [previewState, setPreviewState] = useState<string>("apply");
  const [liveActiveState, setLiveActiveState] = useState<string>("apply");
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [approvals, setApprovals] = useState<any[]>([]);
  const [approving, setApproving] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewers, setReviewers] = useState<any[]>([]);
  const [selectedReviewers, setSelectedReviewers] = useState<any[]>([]);
  const [reviewNote, setReviewNote] = useState("");
  const [requestingReview, setRequestingReview] = useState(false);

  const isAdmin = (currentUser?.Role?.permission_level ?? 0) >= 100;
  const myId = currentUser?.User?.id ?? currentUser?.user_id ?? null;
  // Distinct approvers for the current draft version (server is authoritative; this drives
  // the button label only).
  const distinctApproverCount = new Set(approvals.map((a: any) => a.userId)).size;
  const iApproved = myId != null && approvals.some((a: any) => a.userId === myId);

  const saveSetting = useCallback(
    async (key: string, value: number, apply: (n: number) => void, description: string) => {
      const body = { setting_value: String(value), setting_type: "number" };
      try {
        await axios.put(`/api/adminSettings/settings/${key}`, body);
        apply(value);
        toast.success("Limit updated.");
      } catch (err: any) {
        if (err?.response?.status === 404) {
          try {
            await axios.post(`/api/adminSettings/settings`, {
              setting_key: key,
              setting_value: String(value),
              setting_type: "number",
              description,
              category: "home_content",
            });
            apply(value);
            toast.success("Limit set.");
            return;
          } catch {
            /* fall through to error toast */
          }
        }
        toast.error("Failed to update the limit. You may not have permission.");
      }
    },
    [],
  );

  const fetchDraft = useCallback(async () => {
    const res = await axios.get("/api/home-content/draft");
    setDoc(res.data.document);
    setSavedDoc(JSON.parse(JSON.stringify(res.data.document)));
    setVersion(res.data.version);
  }, []);

  const fetchPublished = useCallback(async () => {
    try {
      const res = await axios.get("/api/home-content");
      setPublishedDoc(res.data?.document || null);
      if (res.data?.activeState) setLiveActiveState(res.data.activeState);
    } catch {
      setPublishedDoc(null);
    }
  }, []);

  const fetchApprovalStatus = useCallback(async () => {
    try {
      const res = await axios.get("/api/home-content/publish/status");
      setApprovals(res.data.pendingApprovals || []);
    } catch {
      setApprovals([]);
    }
  }, []);

  const fetchLeaders = useCallback(async () => {
    const res = await axios.get("/api/home-content/leaders");
    setLeaders(res.data);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await Promise.all([
          fetchDraft(),
          fetchLeaders(),
          fetchPublished(),
          fetchApprovalStatus(),
          axios.get("/api/adminSettings/settings").then((r) => {
            const cap = r.data.find((s: any) => s.setting_key === "home_project_caption_limit");
            const q = r.data.find((s: any) => s.setting_key === "home_leader_quote_limit");
            if (cap) setCaptionLimit(parseInt(cap.setting_value, 10) || 200);
            if (q) setQuoteLimit(parseInt(q.setting_value, 10) || 500);
          }),
          axios
            .get("/api/project_fair")
            .then((r) => {
              const flat = (r.data || [])
                .flatMap((f: any) => f.ApprovedProjects || [])
                .map((p: any) => ({ value: String(p.id), display: `${p.title} (${p.year})`, project: p }));
              setProjectOptions(flat);
            })
            .catch(() => setProjectOptions([])),
        ]);
      } catch {
        toast.error("Failed to load Home Content. Please refresh and try again.");
      } finally {
        setLoading(false);
      }
    })();
  }, [fetchDraft, fetchLeaders, fetchPublished, fetchApprovalStatus]);

  /** Shallow-set a section field, keeping the rest of the document intact. */
  const setSection = (section: string, patch: any) => {
    setDoc((prev: any) => ({ ...prev, [section]: { ...(prev?.[section] || {}), ...patch } }));
  };

  /**
   * Reorder a movable middle section (mission/sessions/projects/leaders) by
   * swapping it with its neighbor. Hero and Join Us are pinned bookends and are
   * never written into `sectionOrder`. The write rides the normal draft flow so
   * the change indicator and publish/discard pick it up automatically.
   */
  const moveSection = (key: string, dir: number) => {
    const order = normalizeSectionOrder(doc?.sectionOrder);
    const i = order.indexOf(key as any);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= order.length) return;
    const next = [...order];
    [next[i], next[j]] = [next[j], next[i]];
    setDoc((prev: any) => ({ ...prev, sectionOrder: next }));
  };

  const saveDraft = async (nextDoc?: any) => {
    const body = { document: nextDoc || doc, version };
    try {
      const res = await axios.put("/api/home-content/draft", body);
      setDoc(res.data.document);
      setSavedDoc(JSON.parse(JSON.stringify(res.data.document)));
      setVersion(res.data.version);
      // A save bumps the draft version, which clears any outstanding approvals server-side.
      setApprovals([]);
      toast.success("Draft saved.");
      return true;
    } catch (err: any) {
      if (err?.response?.status === 409) {
        toast.error("Someone else edited this draft. Reloading the latest version.");
        await fetchDraft();
        await fetchApprovalStatus();
      } else {
        toast.error("Failed to save the draft. Please try again.");
      }
      return false;
    }
  };

  // ADMIN direct publish. Persists any in-memory draft state first, then publishes.
  const publish = async () => {
    setConfirmPublish(false);
    try {
      setPublishing(true);
      const saved = await saveDraft();
      if (!saved) return;
      const res = await axios.post("/api/home-content/publish");
      setApprovals([]);
      if (res.data?.document) setPublishedDoc(res.data.document);
      toast.success("Home page published — the changes are now live.");
    } catch {
      toast.error("Failed to publish. Please try again.");
    } finally {
      setPublishing(false);
    }
  };

  // OFFICER approval vote. Does NOT save the draft (a save would bump the version and clear
  // approvals); it approves the current persisted draft version. When this is the second
  // distinct approval, the server publishes atomically.
  const approve = async () => {
    try {
      setApproving(true);
      const res = await axios.post("/api/home-content/publish/approve", { version });
      if (res.data?.published) {
        setApprovals([]);
        await Promise.all([fetchDraft(), fetchPublished()]);
        toast.success("Second approval received — the home page is now live.");
      } else {
        setApprovals(res.data?.pendingApprovals || []);
        toast.success("Your approval was recorded. A second officer must approve to publish.");
      }
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 409) {
        toast.error("The draft changed since you loaded it. Reloading the latest version.");
        await fetchDraft();
        await fetchApprovalStatus();
      } else {
        toast.error("Failed to record your approval. Please try again.");
      }
    } finally {
      setApproving(false);
    }
  };

  // OFFICER. Retract my own pending approval so I can edit and re-submit. Does not publish.
  const withdrawApproval = async () => {
    try {
      setApproving(true);
      const res = await axios.post("/api/home-content/publish/withdraw-approval", { version });
      setApprovals(res.data?.pendingApprovals || []);
      toast.success("Your approval was withdrawn. You can edit the draft and re-submit.");
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 409) {
        toast.error("The draft changed since you loaded it. Reloading the latest version.");
        await fetchDraft();
        await fetchApprovalStatus();
      } else {
        toast.error("Failed to withdraw your approval. Please try again.");
      }
    } finally {
      setApproving(false);
    }
  };

  const openReview = async () => {
    setReviewOpen(true);
    setSelectedReviewers([]);
    setReviewNote("");
    try {
      const res = await axios.get("/api/home-content/reviewers");
      // Exclude the requester from their own picker.
      setReviewers(
        (res.data || [])
          .filter((r: any) => r.id !== myId)
          // prism-typeahead only matches string option values; numeric ids can be
          // searched but never become selected. Keep the numeric id for the API.
          .map((r: any) => ({ value: String(r.id), id: r.id, label: `${r.name} (${r.email})` })),
      );
    } catch {
      toast.error("Failed to load reviewers.");
      setReviewers([]);
    }
  };

  const submitReview = async () => {
    if (selectedReviewers.length === 0) {
      toast.error("Choose at least one reviewer, or cancel.");
      return;
    }
    try {
      setRequestingReview(true);
      const recipientIds = selectedReviewers.map((r: any) => r.id);
      const res = await axios.post("/api/home-content/publish/request-review", {
        recipientIds,
        note: reviewNote || undefined,
      });
      const sent = res.data?.requested?.length || 0;
      const failed = res.data?.failed?.length || 0;
      const ineligible = res.data?.ineligible?.length || 0;
      if (sent > 0) toast.success(`Review request sent to ${sent} reviewer${sent === 1 ? "" : "s"}.`);
      if (failed > 0) toast.error(`${failed} review email${failed === 1 ? "" : "s"} could not be sent.`);
      if (ineligible > 0) toast.error(`${ineligible} recipient${ineligible === 1 ? "" : "s"} were not eligible reviewers.`);
      setReviewOpen(false);
    } catch {
      toast.error("Failed to send the review request. Please try again.");
    } finally {
      setRequestingReview(false);
    }
  };

  // Open a section editor, snapshotting the document so Cancel/X can revert.
  const openSection = (key: Section) => {
    setSectionSnapshot(JSON.parse(JSON.stringify(doc)));
    setActiveSection(key);
  };
  // Keep the edits: persist them to the draft so they survive navigation. They only go
  // live on the public home page after Publish.
  const doneSection = async () => {
    // Compare against the snapshot taken when the modal opened; if nothing changed,
    // close without a redundant save + "Draft saved." toast.
    const unchanged =
      sectionSnapshot !== null &&
      JSON.stringify(sectionSnapshot) === JSON.stringify(doc);
    setSectionSnapshot(null);
    setActiveSection(null);
    if (unchanged) return;
    await saveDraft();
  };
  // Discard this modal's edits by restoring the snapshot taken when it opened.
  const cancelSection = async () => {
    const snapshot = sectionSnapshot;
    const wasLeaders = activeSection === "leaders";
    setSectionSnapshot(null);
    setActiveSection(null);
    if (!snapshot) return;

    // Leaders are the one section whose edits (add/update/delete/reorder) hit `home_leaders`
    // immediately and permanently, so Cancel can't just roll back the in-memory doc — the
    // rows are already gone. Actively restore the live roster to the snapshot captured when
    // the editor opened (recreating removed leaders, reverting edits), then rebuild the doc
    // snapshot from the restored roster so the card, editor, and Preview all agree again.
    if (wasLeaders) {
      try {
        const res = await axios.post("/api/home-content/leaders/restore", {
          items: snapshot.leaders?.items || [],
        });
        setLeaders(res.data);
        const restored = JSON.parse(JSON.stringify(snapshot));
        if (restored.leaders) restored.leaders.items = leaderSnapshotItems(res.data);
        setDoc(restored);
      } catch {
        toast.error("Failed to restore leaders. Please reload the page.");
        setDoc(snapshot);
      }
      return;
    }
    setDoc(snapshot);
  };
  // Soft reset: replace the active section with the currently-published version, so an
  // officer who lost track of their tweaks can return to the live baseline. Stays in the
  // modal; still needs Done/Publish to persist.
  const resetSectionToPublished = (section: Section) => {
    if (!publishedDoc || !(section in publishedDoc)) {
      toast.info("No published version is available to reset to.");
      return;
    }
    setDoc((prev: any) => ({
      ...prev,
      [section]: JSON.parse(JSON.stringify(publishedDoc[section])),
    }));
    toast.success("Reset to the published version.");
  };

  const discard = async () => {
    setConfirmDiscard(false);
    try {
      const res = await axios.post("/api/home-content/draft/reset");
      setDoc(res.data.document);
      setSavedDoc(JSON.parse(JSON.stringify(res.data.document)));
      setVersion(res.data.version);
      setApprovals([]);
      await fetchLeaders();
      toast.success("Draft changes discarded.");
    } catch {
      toast.error("Failed to discard draft changes.");
    }
  };

  /** Upload a section/overlay image to the draft namespace; returns the imageKey. */
  const uploadImage = async (section: string, file: File): Promise<string | null> => {
    try {
      const data = await readFileAsDataUrl(file);
      const res = await axios.post("/api/home-content/images", {
        image: { data, name: file.name },
        section,
      });
      return res.data.imageKey;
    } catch {
      toast.error("Image upload failed.");
      return null;
    }
  };

  const [videoUploading, setVideoUploading] = useState(false);

  /**
   * Upload a video directly to S3 via a presigned URL (bypasses the app server's body
   * limit), then return the client-usable /api/files path to store in hero.video.
   */
  const uploadVideo = async (file: File): Promise<string | null> => {
    const contentType = file.type || "video/mp4";
    if (!["video/mp4", "video/webm"].includes(contentType)) {
      toast.error("Please choose an MP4 or WebM video.");
      return null;
    }
    try {
      setVideoUploading(true);
      const { data } = await axios.post("/api/home-content/video-upload-url", {
        filename: file.name,
        contentType,
      });
      // PUT the raw file straight to S3 (no auth header — the presigned URL carries it).
      await axios.put(data.uploadUrl, file, {
        headers: { "Content-Type": contentType },
        transformRequest: [(d) => d],
      });
      toast.success("Video uploaded.");
      return data.videoPath as string;
    } catch (err: any) {
      const status = err?.response?.status;
      toast.error(
        status === 403 || status === 0
          ? "Video upload failed — the storage bucket may need a CORS rule for browser uploads."
          : "Video upload failed. Please try again.",
      );
      return null;
    } finally {
      setVideoUploading(false);
    }
  };

  // ---- Leaders CRUD (home_leaders); keeps doc.leaders.items snapshot in sync ----

  const syncLeaderSnapshot = (list: any[]) => {
    setSection("leaders", { items: leaderSnapshotItems(list) });
  };

  const saveLeader = async (leader: any, file?: File) => {
    const payload: any = {
      name: leader.name,
      title: leader.title,
      quote: leader.quote,
      display_order: leader.display_order,
      active: leader.active,
    };
    if (file) {
      payload.headshot = { data: await readFileAsDataUrl(file), name: file.name };
    }
    if (leader.id) {
      await axios.put(`/api/home-content/leaders/${leader.id}`, payload);
    } else {
      await axios.post("/api/home-content/leaders", payload);
    }
    const res = await axios.get("/api/home-content/leaders");
    setLeaders(res.data);
    syncLeaderSnapshot(res.data);
    toast.success("Leader saved.");
  };

  const deleteLeader = async (id: number) => {
    await axios.delete(`/api/home-content/leaders/${id}`);
    const res = await axios.get("/api/home-content/leaders");
    setLeaders(res.data);
    syncLeaderSnapshot(res.data);
    toast.success("Leader removed.");
  };

  // Persist a new leader ordering: write each leader's display_order to its array index,
  // then refetch + resync so the card diff and public snapshot reflect the new order.
  const reorderLeaders = async (orderedIds: number[]) => {
    try {
      await Promise.all(
        orderedIds.map((id, idx) =>
          axios.put(`/api/home-content/leaders/${id}`, { display_order: idx }),
        ),
      );
      const res = await axios.get("/api/home-content/leaders");
      setLeaders(res.data);
      syncLeaderSnapshot(res.data);
      toast.success("Leader order updated.");
    } catch {
      toast.error("Failed to reorder leaders. Please try again.");
    }
  };

  if (loading) {
    return <div className="home-content-loading">Loading Home Content…</div>;
  }
  if (!doc) {
    return <div className="home-content-loading">No home content draft available.</div>;
  }

  // A section is "changed" when the draft differs from the published version. With no
  // published version yet, every populated section counts as an unpublished change.
  const sectionChanged = (key: Section): boolean => {
    if (!publishedDoc) return true;
    return stableStringify(doc?.[key]) !== stableStringify(publishedDoc?.[key]);
  };

  // Unsaved pane-level edits (reordering the section cards is the only mutation here that
  // isn't auto-persisted the way section modals are on Done). Drives the Save button and
  // the "unsaved changes" warning so a reorder can't be silently lost on navigation.
  const hasUnsavedChanges =
    !!doc && !!savedDoc && stableStringify(doc) !== stableStringify(savedDoc);

  // Does the working draft differ from what's currently live? This is the real "is there
  // anything to publish or discard" signal. With no published baseline yet, any draft
  // counts. When false, there's nothing to save, discard, publish, or approve — so we hide
  // the Discard/Save affordances and disable the publish/approval controls.
  const draftDiffersFromPublished =
    !!doc && (!publishedDoc || stableStringify(doc) !== stableStringify(publishedDoc));

  // Officers approve/request-review against the *saved* server draft, so those actions need
  // real, saved changes and no dangling unsaved edits (which would otherwise be silently
  // excluded). Admins publish through a save-first flow, so they only need a live delta.
  const canSubmitForPublish = draftDiffersFromPublished && !hasUnsavedChanges;


  return (
    <AdminPane as="div" width="standard" className="home-content-pane">
      <PrismBanner tone="info" layout="inline">
        <strong>Home Content:</strong>
        <ul style={{ paddingLeft: "1.5rem", marginBottom: 0, marginTop: "0.5rem", listStyleType: "disc" }}>
          <li>Edit the public home page here — your edits stay a private <strong>draft</strong> until published. Cards with pending edits are flagged <strong>● Unpublished changes</strong>.</li>
          <li>Reorder the section cards with the <strong>↑ / ↓</strong> arrows (Hero and Join Us stay pinned). A reorder isn&apos;t saved until you click <strong>Save changes</strong> — leaving the page first loses it.</li>
          <li>Use <strong>Preview</strong> to see exactly how the page will look, including the <strong>Preview as</strong> control to check each &ldquo;Join Us&rdquo; message.</li>
          <li><strong>Admins</strong> publish directly; <strong>officers</strong> publish once a second officer approves — with an optional <strong>Request review</strong> to notify one, and <strong>Withdraw approval</strong> to take back your own vote and keep editing.</li>
          <li>Publish, approval, and review stay disabled until the draft actually differs from the live page. Images keep the site&apos;s artistic filter automatically.</li>
        </ul>
      </PrismBanner>

      <div className="home-content-actions">
        <PrismButton
          tone="secondary"
          display="outline"
          onPrismClick={() => {
            const initial =
              doc?.joinUs?.ctaMode === "override" && doc?.joinUs?.ctaOverrideState
                ? doc.joinUs.ctaOverrideState
                : liveActiveState;
            setPreviewState(initial);
            setPreview(true);
          }}
        >
          Preview
        </PrismButton>
        {draftDiffersFromPublished && (
          <PrismButton tone="danger" display="outline" onPrismClick={() => setConfirmDiscard(true)}>
            Discard Changes
          </PrismButton>
        )}
        {hasUnsavedChanges && (
          <PrismButton
            tone="success"
            display="fill"
            data-testid="save-changes"
            disabled={publishing}
            onPrismClick={() => saveDraft()}
          >
            Save changes
          </PrismButton>
        )}
        <div className="home-content-actions-primary">
          {isAdmin ? (
            <PrismButton
              tone="primary-brand"
              display="fill"
              disabled={publishing || !draftDiffersFromPublished}
              loading={publishing}
              onPrismClick={() => setConfirmPublish(true)}
            >
              Publish
            </PrismButton>
          ) : (
            <>
              <PrismButton
                tone="primary"
                display="outline"
                disabled={!canSubmitForPublish}
                onPrismClick={openReview}
              >
                Request review
              </PrismButton>
              {iApproved && distinctApproverCount < 2 ? (
                <PrismButton
                  tone="caution"
                  display="outline"
                  disabled={approving}
                  loading={approving}
                  onPrismClick={withdrawApproval}
                >
                  Withdraw approval
                </PrismButton>
              ) : (
                <PrismButton
                  tone="primary-brand"
                  display="fill"
                  disabled={approving || !canSubmitForPublish}
                  loading={approving}
                  onPrismClick={approve}
                >
                  {/* Wrapped so the conditional never replaces the host's only child. */}
                  <span>
                    {distinctApproverCount >= 1 ? "Approve & Publish" : "Approve for publish"}
                  </span>
                </PrismButton>
              )}
            </>
          )}
        </div>
      </div>

      {!draftDiffersFromPublished && (
        <div className="home-content-nochanges-note" role="status" data-testid="nochanges-note">
          No changes to publish yet — the draft matches the live page. Edit a section or
          reorder the cards to enable {isAdmin ? "publishing" : "review and approval"}.
        </div>
      )}

      {hasUnsavedChanges && (
        <div className="home-content-unsaved-note" role="status" data-testid="unsaved-note">
          You&apos;ve reordered sections but haven&apos;t saved yet. Click <strong>Save changes</strong> to
          keep the new order — it will be lost if you leave this page without saving.
        </div>
      )}

      <div className="home-content-approval-status" data-testid="approval-status">
        {isAdmin ? (
          distinctApproverCount === 0 ? (
            <span>
              You're editing draft version {version}. As an admin you can publish directly — no
              officer approvals are pending.
            </span>
          ) : (
            <span>
              Draft version {version} — {distinctApproverCount} of 2 officer approvals so far
              {approvals.length > 0 && (
                <> — approved by {approvals.map((a: any) => a.name || `user ${a.userId}`).join(", ")}</>
              )}
              . As an admin you can publish directly, or wait for a second officer to approve.
            </span>
          )
        ) : distinctApproverCount === 0 ? (
          <span>No approvals yet — publishing requires two officers to approve the current draft (version {version}).</span>
        ) : (
          <span>
            {distinctApproverCount} of 2 approvals for draft version {version}
            {approvals.length > 0 && (
              <> — approved by {approvals.map((a: any) => a.name || `user ${a.userId}`).join(", ")}</>
            )}
            . {distinctApproverCount >= 2 ? "Ready to publish." : "One more officer needs to approve."}
            {iApproved && distinctApproverCount < 2 && (
              <> Spotted a problem? Use <strong>Withdraw approval</strong> to edit and re-submit.</>
            )}
          </span>
        )}
      </div>

      <div className="home-content-sections">
        {(() => {
          const labels: Record<Section, [string, string]> = {
            hero: ["🎬 Hero", doc.hero?.title],
            mission: ["🎯 Mission", doc.mission?.heading],
            sessions: ["📅 Sessions", summarizeCount(doc.sessions?.items?.length || 0, "session")],
            projects: ["💡 Projects", summarizeCount(doc.projects?.items?.length || 0, "project")],
            leaders: ["👥 Leaders", summarizeCount(doc.leaders?.items?.length || 0, "leader")],
            joinUs: ["🙌 Join Us", doc.joinUs?.ctaMode === "override" ? "manual override" : "auto"],
          };
          const order = normalizeSectionOrder(doc?.sectionOrder);
          const cards: { key: Section; movable: boolean; canUp: boolean; canDown: boolean }[] = [
            { key: "hero", movable: false, canUp: false, canDown: false },
            ...order.map((key, i) => ({
              key: key as Section,
              movable: true,
              canUp: i > 0,
              canDown: i < order.length - 1,
            })),
            { key: "joinUs", movable: false, canUp: false, canDown: false },
          ];
          return cards.map(({ key, movable, canUp, canDown }) => {
            const [label, summary] = labels[key];
            const changed = sectionChanged(key);
            return (
              <div
                className={`home-content-section-card${changed ? " home-content-section-card--changed" : ""}`}
                key={key}
              >
                <div>
                  <div className="home-content-section-title">
                    {label}
                    {changed && (
                      <span className="home-content-changed-badge" title="This section has unpublished changes">
                        ● Unpublished changes
                      </span>
                    )}
                  </div>
                  <div className="home-content-section-summary">{summary}</div>
                </div>
                <div className="home-content-section-controls">
                  <div className="home-content-section-reorder" aria-hidden={!movable}>
                    {movable ? (
                      <>
                        <PrismButton
                          size="sm"
                          tone="secondary"
                          display="outline"
                          className="home-content-reorder-btn"
                          disabled={!canUp}
                          title="Move section up"
                          setAriaLabel={`Move ${label} up`}
                          onPrismClick={() => moveSection(key, -1)}
                        >
                          ↑
                        </PrismButton>
                        <PrismButton
                          size="sm"
                          tone="secondary"
                          display="outline"
                          className="home-content-reorder-btn"
                          disabled={!canDown}
                          title="Move section down"
                          setAriaLabel={`Move ${label} down`}
                          onPrismClick={() => moveSection(key, 1)}
                        >
                          ↓
                        </PrismButton>
                      </>
                    ) : (
                      <span className="home-content-reorder-pinned" title="This section is pinned in place">
                        📌
                      </span>
                    )}
                  </div>
                  <PrismButton tone="primary" display="outline" onPrismClick={() => openSection(key)}>
                    Edit
                  </PrismButton>
                </div>
              </div>
            );
          });
        })()}
      </div>

      {/* ---- Section modals ---- */}
      <SectionModal
        show={activeSection !== null}
        section={activeSection}
        doc={doc}
        leaders={leaders}
        projectOptions={projectOptions}
        captionLimit={captionLimit}
        quoteLimit={quoteLimit}
        isAdmin={isAdmin}
        onSaveCaptionLimit={(n: number) => saveSetting("home_project_caption_limit", n, setCaptionLimit, "Max characters for a project overlay caption on the home page.")}
        onSaveQuoteLimit={(n: number) => saveSetting("home_leader_quote_limit", n, setQuoteLimit, "Max characters for a leader quote on the home page.")}
        onDone={doneSection}
        onCancel={cancelSection}
        onResetToPublished={resetSectionToPublished}
        canReset={publishedDoc !== null}
        setSection={setSection}
        uploadImage={uploadImage}
        uploadVideo={uploadVideo}
        videoUploading={videoUploading}
        saveLeader={saveLeader}
        deleteLeader={deleteLeader}
        reorderLeaders={reorderLeaders}
      />

      <PrismDialog
        open={confirmPublish}
        close-method="user"
        setAriaLabel="Publish home page?"
        onPrismClose={() => setConfirmPublish(false)}
      >
        <span slot="header">Publish home page?</span>
        This will save your draft and make it the live public home page for all users.
        <PrismButton slot="footer" tone="secondary" display="outline" onPrismClick={() => setConfirmPublish(false)}>
          Cancel
        </PrismButton>
        <PrismButton slot="footer" tone="primary-brand" display="fill" onPrismClick={publish}>
          Publish now
        </PrismButton>
      </PrismDialog>

      <PrismDialog
        open={confirmDiscard}
        close-method="user"
        setAriaLabel="Discard all draft changes?"
        onPrismClose={() => setConfirmDiscard(false)}
      >
        <span slot="header">Discard all draft changes?</span>
        This resets the draft back to the currently-published home page. Every unpublished
        edit across all sections will be lost. This cannot be undone.
        <PrismButton slot="footer" tone="secondary" display="outline" onPrismClick={() => setConfirmDiscard(false)}>
          Cancel
        </PrismButton>
        <PrismButton slot="footer" tone="danger" display="fill" onPrismClick={discard}>
          Discard changes
        </PrismButton>
      </PrismDialog>

      <PrismDialog
        open={reviewOpen}
        close-method="user"
        setAriaLabel="Request a review"
        onPrismClose={() => setReviewOpen(false)}
      >
        <span slot="header">Request a review</span>
        <div className="home-content-dialog-body">
          <p className="home-content-hint">
            Optional — email one or more officers/admins asking them to review the current
            draft. This never publishes anything and is not required to publish.
          </p>
          <div className="home-content-field">
            <label className="home-content-label" htmlFor="home-content-reviewers">Reviewers</label>
            <PrismTypeahead
              label="Reviewers"
              hideLabel
              as="multi"
              options={reviewers}
              placeholder="Search officers/admins by name…"
              onPrismChange={(e: CustomEvent<{ value?: string }>) => {
                // prism-typeahead reports its multi selection as a delimited string of
                // values, so map back to the option objects the submit handler expects.
                const picked = String(e.detail?.value ?? "").split(",").map((v) => v.trim()).filter(Boolean);
                setSelectedReviewers(reviewers.filter((r: any) => picked.includes(String(r.value))));
              }}
            />
          </div>
          <PrismInputTextarea
            label="Note (optional)"
            rows={3}
            value={reviewNote}
            placeholder="Add an optional message for the reviewers…"
            onPrismInput={(e: CustomEvent<{ value?: string }>) => setReviewNote(e.detail?.value || "")}
          />
        </div>
        <PrismButton slot="footer" tone="secondary" display="outline" onPrismClick={() => setReviewOpen(false)}>
          Cancel
        </PrismButton>
        <PrismButton
          slot="footer"
          tone="primary-brand"
          display="fill"
          disabled={requestingReview}
          loading={requestingReview}
          onPrismClick={submitReview}
        >
          Send request
        </PrismButton>
      </PrismDialog>

      {/* Full-screen preview: gives <Home> the true viewport width so it renders exactly
          like the live page (nothing clipped/scaled). The dialog body owns the single
          scrollbar. */}
      <PrismDialog
        open={preview}
        close-method="auto"
        close-label="Close preview"
        className="home-content-preview-dialog"
        setAriaLabel="Preview (draft — not yet published)"
        onPrismClose={() => setPreview(false)}
      >
        <span slot="header">Preview (draft — not yet published)</span>
        <div className="home-content-preview-controls">
          <label className="home-content-preview-state">
            <span>Preview as:</span>{" "}
            <select
              data-testid="preview-state"
              value={previewState}
              onChange={(e) => setPreviewState(e.target.value)}
            >
              {CTA_STATES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                  {s.value === liveActiveState ? " — live now" : ""}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="home-content-preview-body">
          {preview ? (
            <Home overrideDocument={doc} overrideActiveState={previewState} />
          ) : null}
        </div>
      </PrismDialog>
    </AdminPane>
  );
};

// ---------------------------------------------------------------------------

interface SectionModalProps {
  show: boolean;
  section: Section | null;
  doc: any;
  leaders: any[];
  projectOptions: any[];
  captionLimit: number;
  quoteLimit: number;
  isAdmin: boolean;
  onSaveCaptionLimit: (n: number) => Promise<void>;
  onSaveQuoteLimit: (n: number) => Promise<void>;
  onDone: () => void;
  onCancel: () => void;
  onResetToPublished: (section: Section) => void;
  canReset: boolean;
  setSection: (section: string, patch: any) => void;
  uploadImage: (section: string, file: File) => Promise<string | null>;
  uploadVideo: (file: File) => Promise<string | null>;
  videoUploading: boolean;
  saveLeader: (leader: any, file?: File) => Promise<void>;
  deleteLeader: (id: number) => Promise<void>;
  reorderLeaders: (orderedIds: number[]) => Promise<void>;
}

// A titled, bordered container that groups related fields together so a section
// modal reads as a few labelled panels instead of one long run of inputs. Bigger
// lists (sessions, project tiles, leaders) are wrapped as a single group rather
// than fracturing the border at every item.
const FieldGroup: React.FC<{
  title?: string;
  description?: string;
  className?: string;
  children: React.ReactNode;
}> = ({ title, description, className, children }) => (
  <section className={`home-content-group${className ? ` ${className}` : ""}`}>
    {title ? (
      <div className="home-content-group-head">
        <h6 className="home-content-group-title">{title}</h6>
        {description ? <p className="home-content-group-desc">{description}</p> : null}
      </div>
    ) : null}
    <div className="home-content-group-body">{children}</div>
  </section>
);

const FilePicker: React.FC<{
  accept: string;
  onSelect: (file: File) => void;
  label?: string;
  disabled?: boolean;
}> = ({ accept, onSelect, label = "Choose File", disabled = false }) => (
  <PrismInputFile
    className="home-content-filepicker"
    label={label}
    accept={accept}
    disabled={disabled}
    onPrismChange={(e: CustomEvent<{ files?: { list?: FileList } }>) => {
      const file = e.detail?.files?.list?.[0];
      if (file) onSelect(file);
    }}
  />
);

const ImageField: React.FC<{
  label: string;
  section: string;
  imageKey?: string;
  altValue?: string;
  hint?: React.ReactNode;
  onUpload: (section: string, file: File) => Promise<string | null>;
  onChange: (imageKey: string) => void;
  onAltChange?: (alt: string) => void;
}> = ({ label, section, imageKey, altValue, hint, onUpload, onChange, onAltChange }) => (
  <div className="home-content-field">
    <span className="home-content-label">{label}</span>
    {imageKey ? <div><img src={imageKey} alt={altValue || label} className="home-content-thumb" /></div> : null}
    <FilePicker
      accept=".png,.jpg,.jpeg"
      label="Choose Image"
      onSelect={async (file) => {
        const key = await onUpload(section, file);
        if (key) onChange(key);
      }}
    />
    {hint ? <small className="home-content-hint">{hint}</small> : null}
    {onAltChange ? (
      <>
        <PrismInputText
          label="Alt text"
          placeholder="Describe the image for screen readers"
          value={altValue || ""}
          onPrismInput={(e: CustomEvent<{ value?: string }>) => onAltChange(e.detail?.value || "")}
        />
        <small className="home-content-hint">
          Alt text is read by screen readers and shown if the image fails to load.
        </small>
      </>
    ) : null}
  </div>
);

const SectionModal: React.FC<SectionModalProps> = (props) => {
  const { show, section, doc, leaders, projectOptions, captionLimit, quoteLimit, isAdmin, onSaveCaptionLimit, onSaveQuoteLimit, onDone, onCancel, onResetToPublished, canReset, setSection, uploadImage, uploadVideo, videoUploading, saveLeader, deleteLeader, reorderLeaders } = props;
  if (!section) return null;

  const titleMap: Record<Section, string> = {
    hero: "Edit Hero",
    mission: "Edit Mission",
    sessions: "Edit Sessions",
    projects: "Edit Projects",
    leaders: "Edit Leaders",
    joinUs: "Edit Join Us",
  };

  return (
    <PrismDialog
      open={show}
      close-method="user"
      setAriaLabel={titleMap[section]}
      onPrismClose={onCancel}
    >
      <span slot="header">{titleMap[section]}</span>
      <div className="home-content-dialog-body">
        {section === "hero" && (
          <>
            <FieldGroup title="Hero title" description="The headline over the hero, and how boldly it sits on the background.">
              <PrismInputText
                label="Hero title"
                value={doc.hero?.title || ""}
                onPrismInput={(e: CustomEvent<{ value?: string }>) => setSection("hero", { title: e.detail?.value || "" })}
              />
              <div className="home-content-field">
                <label className="home-content-label" htmlFor="home-content-hero-title-opacity">
                  Hero title opacity: {Math.round((doc.hero?.titleOpacity ?? 1) * 100)}%
                </label>
                <input
                  id="home-content-hero-title-opacity"
                  type="range"
                  className="home-content-range"
                  min={0}
                  max={100}
                  step={5}
                  value={Math.round((doc.hero?.titleOpacity ?? 1) * 100)}
                  onChange={(e: any) => setSection("hero", { titleOpacity: parseInt(e.target.value, 10) / 100 })}
                />
                <small className="home-content-hint">
                  Adjusts how solid or faded the hero title text appears — 0% is fully
                  transparent, 100% is fully solid.
                </small>
              </div>
            </FieldGroup>
            <FieldGroup title="Background & video" description="The splatter graphic behind the hero and the sizzle reel on the play button.">
              <div className="home-content-field">
                <label className="home-content-label" htmlFor="home-content-hero-splatter-opacity">
                  Hero background opacity: {Math.round((doc.hero?.splatterOpacity ?? 1) * 100)}%
                </label>
                <input
                  id="home-content-hero-splatter-opacity"
                  type="range"
                  className="home-content-range"
                  min={0}
                  max={100}
                  step={5}
                  value={Math.round((doc.hero?.splatterOpacity ?? 1) * 100)}
                  onChange={(e: any) => setSection("hero", { splatterOpacity: parseInt(e.target.value, 10) / 100 })}
                />
                <small className="home-content-hint">
                  Adjusts how solid or faded the background splatter graphic behind the hero
                  appears — 0% is fully transparent, 100% is fully solid.
                </small>
              </div>
              <div className="home-content-field">
                {doc.hero?.video ? (
                  <div>
                    <video
                      src={doc.hero.video}
                      className="home-content-video-thumb"
                      controls
                      preload="metadata"
                    />
                  </div>
                ) : null}
                <PrismInputText
                  label="Sizzle reel video"
                  value={doc.hero?.video || ""}
                  placeholder="/api/files/videos/CLEAR_Video.mp4"
                  onPrismInput={(e: CustomEvent<{ value?: string }>) => setSection("hero", { video: e.detail?.value || "" })}
                />
                <FilePicker
                  accept="video/mp4,video/webm,.mp4,.webm"
                  label={videoUploading ? "Uploading…" : "Upload Video"}
                  disabled={videoUploading}
                  onSelect={async (file) => {
                    const path = await uploadVideo(file);
                    if (path) setSection("hero", { video: path });
                  }}
                />
                <small className="home-content-hint">
                  {videoUploading
                    ? "Uploading video…"
                    : "Upload a new MP4/WebM (uploaded directly to storage), or paste a hosted video path above. The video that plays behind the hero's play button."}
                </small>
              </div>
            </FieldGroup>
          </>
        )}

        {section === "mission" && (
          <>
            <FieldGroup title="Text">
              <PrismInputText
                label="Heading"
                value={doc.mission?.heading || ""}
                onPrismInput={(e: CustomEvent<{ value?: string }>) => setSection("mission", { heading: e.detail?.value || "" })}
              />
              <PrismInputText
                label="Tagline"
                value={doc.mission?.tagline || ""}
                onPrismInput={(e: CustomEvent<{ value?: string }>) => setSection("mission", { tagline: e.detail?.value || "" })}
              />
              <div className="home-content-field">
                <span className="home-content-label">Body</span>
                <MessageEditor value={doc.mission?.body || ""} onChange={(v: string) => setSection("mission", { body: v })} />
              </div>
              <PrismInputText
                label="Section quote"
                value={doc.mission?.quote || ""}
                onPrismInput={(e: CustomEvent<{ value?: string }>) => setSection("mission", { quote: e.detail?.value || "" })}
              />
            </FieldGroup>
            <FieldGroup title="Image & display" description="The Mission graphic and how it is rendered on the home page.">
              <ImageField
                label="Section image"
                section="mission"
                imageKey={doc.mission?.imageKey}
                altValue={doc.mission?.imageAlt}
                hint="Landscape works best. The long edge is capped at 1920px and the aspect ratio is kept, so nothing is cropped. Don't add your own vignette or faded edges — the splatter mask feathers the image for you, and a pre-faded photo will double up."
                onUpload={uploadImage}
                onChange={(k) => setSection("mission", { imageKey: k })}
                onAltChange={(a) => setSection("mission", { imageAlt: a })}
              />
              <div className="home-content-field">
                <label className="home-content-label" htmlFor="home-content-mission-image-opacity">
                  Section image opacity: {Math.round((doc.mission?.imageOpacity ?? 1) * 100)}%
                </label>
                <input
                  id="home-content-mission-image-opacity"
                  type="range"
                  className="home-content-range"
                  min={0}
                  max={100}
                  step={5}
                  value={Math.round((doc.mission?.imageOpacity ?? 1) * 100)}
                  onChange={(e: any) => setSection("mission", { imageOpacity: parseInt(e.target.value, 10) / 100 })}
                />
                <small className="home-content-hint">
                  Adjusts how solid or faded the Mission graphic appears — 0% is fully
                  transparent, 100% is fully solid.
                </small>
              </div>
            </FieldGroup>
          </>
        )}

        {section === "sessions" && (
          <SessionsEditor doc={doc} setSection={setSection} uploadImage={uploadImage} />
        )}

        {section === "projects" && (
          <ProjectsEditor doc={doc} setSection={setSection} projectOptions={projectOptions} captionLimit={captionLimit} isAdmin={isAdmin} onSaveCaptionLimit={onSaveCaptionLimit} uploadImage={uploadImage} />
        )}

        {section === "leaders" && (
          <LeadersEditor doc={doc} setSection={setSection} leaders={leaders} quoteLimit={quoteLimit} isAdmin={isAdmin} onSaveQuoteLimit={onSaveQuoteLimit} saveLeader={saveLeader} deleteLeader={deleteLeader} reorderLeaders={reorderLeaders} uploadImage={uploadImage} />
        )}

        {section === "joinUs" && (
          <JoinUsEditor doc={doc} setSection={setSection} uploadImage={uploadImage} />
        )}
      </div>

      {section === "leaders" ? (
        <small slot="footer" className="home-content-hint home-content-dialog-footer-note">
          Note: adding, updating, or removing a leader is applied immediately. Use
          <strong> Cancel</strong> to undo every change you made since opening this editor
          and restore the leaders as they were.
        </small>
      ) : null}
      {section !== "leaders" && canReset ? (
        <PrismButton
          slot="footer"
          className="home-content-dialog-footer-lead"
          tone="caution"
          display="outline"
          onPrismClick={() => onResetToPublished(section)}
        >
          Reset to Published
        </PrismButton>
      ) : null}
      <PrismButton slot="footer" tone="secondary" display="outline" onPrismClick={onCancel}>
        Cancel
      </PrismButton>
      <PrismButton slot="footer" tone="primary-brand" display="fill" onPrismClick={onDone}>
        Done
      </PrismButton>
    </PrismDialog>
  );
};

const SessionsEditor: React.FC<any> = ({ doc, setSection, uploadImage }) => {
  const items = doc.sessions?.items || [];
  const update = (next: any[]) => setSection("sessions", { items: next });
  const move = (i: number, dir: number) => {
    const next = [...items];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    next.forEach((it, idx) => (it.order = idx + 1));
    update(next);
  };
  return (
    <>
      <FieldGroup title="Section presentation" description="The quote and image shown alongside the Sessions section on the home page.">
        <PrismInputText
          label="Section quote"
          value={doc.sessions?.quote || ""}
          onPrismInput={(e: CustomEvent<{ value?: string }>) => setSection("sessions", { quote: e.detail?.value || "" })}
        />
        <ImageField label="Section image" section="sessions" imageKey={doc.sessions?.imageKey} onUpload={uploadImage} onChange={(k) => setSection("sessions", { imageKey: k })} />
      </FieldGroup>
      <FieldGroup title="Sessions" description="Each entry is one info-session row. Reorder with the arrows.">
        {items.map((item: any, i: number) => (
          <div className="home-content-list-item" key={item.id || i}>
            <PrismInputText
              label="Session title"
              placeholder="Session title"
              value={item.term || ""}
              onPrismInput={(e: CustomEvent<{ value?: string }>) => {
                const next = [...items];
                next[i] = { ...item, term: e.detail?.value || "" };
                update(next);
              }}
            />
            <MessageEditor
              value={item.description || ""}
              onChange={(v: string) => {
                const next = [...items];
                next[i] = { ...item, description: v };
                update(next);
              }}
            />
            <div className="home-content-list-controls">
              <PrismButton size="sm" tone="secondary" display="outline" setAriaLabel="Move session up" onPrismClick={() => move(i, -1)}>↑</PrismButton>
              <PrismButton size="sm" tone="secondary" display="outline" setAriaLabel="Move session down" onPrismClick={() => move(i, 1)}>↓</PrismButton>
              <PrismButton size="sm" tone="danger" display="outline" onPrismClick={() => update(items.filter((_: any, k: number) => k !== i))}>Remove</PrismButton>
            </div>
          </div>
        ))}
        <div className="home-content-add-row">
          <PrismButton tone="primary" display="outline" onPrismClick={() => update([...items, { id: `s${Date.now()}`, term: "New Session", description: "", order: items.length + 1 }])}>
            + Add Session
          </PrismButton>
        </div>
      </FieldGroup>
    </>
  );
};

const CharLimitField: React.FC<{
  label: string;
  value: number;
  isAdmin: boolean;
  onSave: (n: number) => Promise<void>;
}> = ({ label, value, isAdmin, onSave }) => {
  const [draft, setDraft] = useState(String(value));
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  if (!isAdmin) {
    return (
      <div className="home-content-field">
        <span className="home-content-label">{label}</span>
        <div className="home-content-limit-readonly">
          {value} characters <span className="home-content-hint">(configurable by an admin)</span>
        </div>
      </div>
    );
  }

  const parsed = parseInt(draft, 10);
  const invalid = !parsed || parsed < 1;
  return (
    <div className="home-content-field">
      <div className="home-content-limit-edit">
        <PrismInputText
          label={label}
          type="number"
          min={1}
          value={draft}
          className="home-content-limit-input"
          onPrismInput={(e: CustomEvent<{ value?: string | number }>) => setDraft(String(e.detail?.value ?? ""))}
        />
        <PrismButton
          size="sm"
          tone="primary"
          display="outline"
          disabled={busy || invalid || parsed === value}
          onPrismClick={async () => {
            if (invalid) return;
            setBusy(true);
            try {
              await onSave(parsed);
            } finally {
              setBusy(false);
            }
          }}
        >
          Save limit
        </PrismButton>
      </div>
    </div>
  );
};

const ProjectsEditor: React.FC<any> = ({ doc, setSection, projectOptions, captionLimit, isAdmin, onSaveCaptionLimit, uploadImage }) => {
  const items = doc.projects?.items || [];
  const update = (next: any[]) => setSection("projects", { items: next });
  const [pendingRemove, setPendingRemove] = React.useState<number | null>(null);
  const move = (i: number, dir: number) => {
    const next = [...items];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    next.forEach((it: any, idx: number) => (it.order = idx + 1));
    update(next);
  };
  return (
    <>
      <FieldGroup title="Section presentation" description="The intro, quote, and image shown with the Projects section on the home page.">
        <PrismInputText
          label="Intro"
          value={doc.projects?.intro || ""}
          onPrismInput={(e: CustomEvent<{ value?: string }>) => setSection("projects", { intro: e.detail?.value || "" })}
        />
        <PrismInputText
          label="Section quote"
          value={doc.projects?.quote || ""}
          onPrismInput={(e: CustomEvent<{ value?: string }>) => setSection("projects", { quote: e.detail?.value || "" })}
        />
        <ImageField label="Section image" section="projects" imageKey={doc.projects?.imageKey} onUpload={uploadImage} onChange={(k) => setSection("projects", { imageKey: k })} />
      </FieldGroup>
      <FieldGroup title="Project tiles" description="The tiles shown in the projects grid. Reorder with the arrows.">
        <CharLimitField label="Caption character limit" value={captionLimit} isAdmin={isAdmin} onSave={onSaveCaptionLimit} />
        {items.map((item: any, i: number) => {
          const established = !!item.title;
          return (
          <div className="home-content-list-item" key={item.id || i}>
            {established ? (
              <div className="home-content-project-title-row">
                <span className="home-content-project-title">{item.title}</span>
              </div>
            ) : (
              <div className="home-content-field">
                <label className="home-content-label" htmlFor={`home-content-project-pick-${i}`}>Pick an existing project</label>
                <PrismSelect
                  label="Pick an existing project"
                  hideLabel
                  value={item.projectId ? String(item.projectId) : ""}
                  options={[{ value: "", display: "Select a project…" }, ...projectOptions]}
                  onPrismChange={(e: CustomEvent<{ value?: string }>) => {
                    const picked = String(e.detail?.value ?? "");
                    const opt = projectOptions.find((o: any) => String(o.value) === picked);
                    const next = [...items];
                    next[i] = opt
                      ? {
                          ...item,
                          // The option's value is a string because prism-select only
                          // round-trips strings; the stored document keeps the numeric id.
                          projectId: opt.project.id,
                          title: opt.project.title,
                          captionOverride: item.captionOverride || opt.project.description || "",
                          imageKey: resolveProjectLogo(opt.project) || item.imageKey || "",
                        }
                      : { ...item, projectId: undefined };
                    update(next);
                  }}
                />
              </div>
            )}
            <PrismInputTextarea
              label="Overlay caption (home-only; does not change the project)"
              rows={6}
              value={item.captionOverride || item.description || ""}
              onPrismInput={(e: CustomEvent<{ value?: string }>) => {
                const next = [...items];
                next[i] = item.projectId
                  ? { ...item, captionOverride: e.detail?.value || "" }
                  : { ...item, description: e.detail?.value || "" };
                update(next);
              }}
            />
            <CharCounter value={item.captionOverride || item.description || ""} limit={captionLimit} label="Caption" />
            <div className="home-content-tile-image-row">
              {item.imageKey ? (
                <img src={item.imageKey} alt={item.title || "Tile image"} className="home-content-thumb" />
              ) : (
                <div className="home-content-thumb home-content-thumb--empty">No image</div>
              )}
              {item.projectId ? (
                <span className="home-content-hint">Image comes from the selected project&apos;s logo.</span>
              ) : (
                <FilePicker
                  accept=".png,.jpg,.jpeg"
                  label="Choose Image"
                  onSelect={async (file) => {
                    const key = await uploadImage("projects", file);
                    if (key) {
                      const next = [...items];
                      next[i] = { ...item, imageKey: key };
                      update(next);
                    }
                  }}
                />
              )}
            </div>
            <div className="home-content-list-controls">
              <PrismButton size="sm" tone="secondary" display="outline" disabled={i === 0} title="Move up" setAriaLabel="Move tile up" onPrismClick={() => move(i, -1)}>↑</PrismButton>
              <PrismButton size="sm" tone="secondary" display="outline" disabled={i === items.length - 1} title="Move down" setAriaLabel="Move tile down" onPrismClick={() => move(i, 1)}>↓</PrismButton>
              {pendingRemove === i ? (
                <>
                  <span className="home-content-remove-confirm-label">Remove this tile?</span>
                  <PrismButton size="sm" tone="danger" display="fill" onPrismClick={() => { update(items.filter((_: any, k: number) => k !== i)); setPendingRemove(null); }}>Yes, remove</PrismButton>
                  <PrismButton size="sm" tone="secondary" display="outline" onPrismClick={() => setPendingRemove(null)}>Cancel</PrismButton>
                </>
              ) : (
                <PrismButton size="sm" tone="danger" display="outline" className="home-content-remove-btn" onPrismClick={() => setPendingRemove(i)}>Remove tile</PrismButton>
              )}
            </div>
          </div>
          );
        })}
        <div className="home-content-add-row">
          <PrismButton tone="primary" display="outline" onPrismClick={() => update([...items, { id: `p${Date.now()}`, projectId: undefined, captionOverride: "", order: items.length + 1 }])}>
            + Add project tile
          </PrismButton>
        </div>
      </FieldGroup>
    </>
  );
};

const LeaderRow: React.FC<any> = ({ leader, index, count, quoteLimit, onSave, onDelete, onMove }) => {
  const [draft, setDraft] = useState({
    name: leader.name || "",
    title: leader.title || "",
    quote: leader.quote || "",
  });
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setDraft({ name: leader.name || "", title: leader.title || "", quote: leader.quote || "" });
    setFile(null);
  }, [leader.id, leader.name, leader.title, leader.quote]);

  const dirty =
    draft.name !== (leader.name || "") ||
    draft.title !== (leader.title || "") ||
    draft.quote !== (leader.quote || "") ||
    !!file;

  return (
    <div className="home-content-list-item">
      <div className="home-content-leader-head">
        {leader.headshot_key ? (
          <img src={leader.headshot_key} alt={leader.name || "Headshot"} className="home-content-thumb" />
        ) : (
          <div className="home-content-thumb home-content-thumb--empty">No photo</div>
        )}
        <div className="home-content-leader-order">
          <PrismButton size="sm" tone="secondary" display="outline" disabled={index === 0} title="Move up" setAriaLabel="Move leader up" onPrismClick={() => onMove(index, -1)}>↑</PrismButton>
          <PrismButton size="sm" tone="secondary" display="outline" disabled={index === count - 1} title="Move down" setAriaLabel="Move leader down" onPrismClick={() => onMove(index, 1)}>↓</PrismButton>
        </div>
      </div>
      <PrismInputText
        label="Name"
        value={draft.name}
        onPrismInput={(e: CustomEvent<{ value?: string }>) => setDraft({ ...draft, name: e.detail?.value || "" })}
      />
      <PrismInputText
        label="Title"
        value={draft.title}
        onPrismInput={(e: CustomEvent<{ value?: string }>) => setDraft({ ...draft, title: e.detail?.value || "" })}
      />
      <PrismInputTextarea
        label="Quote"
        rows={4}
        value={draft.quote}
        onPrismInput={(e: CustomEvent<{ value?: string }>) => setDraft({ ...draft, quote: e.detail?.value || "" })}
      />
      <CharCounter value={draft.quote} limit={quoteLimit} label="Quote" />
      <div className="home-content-field">
        <FilePicker accept=".png,.jpg,.jpeg" label="Replace Headshot" onSelect={(f) => setFile(f)} />
      </div>
      <div className="home-content-list-controls">
        <PrismButton
          size="sm"
          tone="primary"
          display="outline"
          disabled={busy || !dirty}
          onPrismClick={async () => {
            setBusy(true);
            try {
              await onSave({ ...leader, name: draft.name, title: draft.title, quote: draft.quote }, file || undefined);
              setFile(null);
            } finally {
              setBusy(false);
            }
          }}
        >
          Save
        </PrismButton>
        <PrismButton size="sm" tone="danger" display="outline" onPrismClick={() => onDelete(leader.id)}>Remove</PrismButton>
      </div>
    </div>
  );
};

const LeadersEditor: React.FC<any> = ({ doc, setSection, leaders, quoteLimit, isAdmin, onSaveQuoteLimit, saveLeader, deleteLeader, reorderLeaders, uploadImage }) => {
  const [draftLeader, setDraftLeader] = useState<any>({ name: "", title: "", quote: "", display_order: 0, active: true });
  const [file, setFile] = useState<File | null>(null);
  return (
    <>
      <PrismBanner tone="info" layout="inline">
        <strong>Requesting leader updates from Corporate Communications:</strong>
        <ul style={{ paddingLeft: "1.5rem", marginBottom: 0, marginTop: "0.5rem", listStyleType: "disc" }}>
          <li>Full name and current title</li>
          <li>An approved quote (about {quoteLimit} characters or fewer)</li>
          <li>A high-resolution headshot (JPG or PNG)</li>
        </ul>
        Once you have these, add or update the leader below and press Publish.
      </PrismBanner>

      <FieldGroup title="Section presentation" description="The quote, image, and quote-length limit for the Leaders section.">
        <CharLimitField label="Quote character limit" value={quoteLimit} isAdmin={isAdmin} onSave={onSaveQuoteLimit} />
        <PrismInputTextarea
          label="Section quote"
          rows={3}
          value={doc.leaders?.quote || ""}
          onPrismInput={(e: CustomEvent<{ value?: string }>) => setSection("leaders", { quote: e.detail?.value || "" })}
        />
        <ImageField label="Section image" section="leaders" imageKey={doc.leaders?.imageKey} onUpload={uploadImage} onChange={(k) => setSection("leaders", { imageKey: k })} />
      </FieldGroup>

      <FieldGroup title="Current leaders" description="Adding, updating, removing, or reordering a leader saves immediately.">
        {leaders.length === 0 ? (
          <p className="home-content-hint">No leaders yet. Add one below.</p>
        ) : null}
        {leaders.map((l: any, i: number) => (
          <LeaderRow
            key={l.id}
            leader={l}
            index={i}
            count={leaders.length}
            quoteLimit={quoteLimit}
            onSave={saveLeader}
            onDelete={deleteLeader}
            onMove={(idx: number, dir: number) => {
              const arr = [...leaders];
              const j = idx + dir;
              if (j < 0 || j >= arr.length) return;
              [arr[idx], arr[j]] = [arr[j], arr[idx]];
              reorderLeaders(arr.map((x: any) => x.id));
            }}
          />
        ))}
      </FieldGroup>

      <FieldGroup title="Add a leader">
        <PrismInputText
          label="Name"
          placeholder="Name"
          value={draftLeader.name}
          onPrismInput={(e: CustomEvent<{ value?: string }>) => setDraftLeader({ ...draftLeader, name: e.detail?.value || "" })}
        />
        <PrismInputText
          label="Title"
          placeholder="Title"
          value={draftLeader.title}
          onPrismInput={(e: CustomEvent<{ value?: string }>) => setDraftLeader({ ...draftLeader, title: e.detail?.value || "" })}
        />
        <PrismInputTextarea
          label="Quote"
          rows={3}
          placeholder="Quote"
          value={draftLeader.quote}
          onPrismInput={(e: CustomEvent<{ value?: string }>) => setDraftLeader({ ...draftLeader, quote: e.detail?.value || "" })}
        />
        <CharCounter value={draftLeader.quote} limit={quoteLimit} label="Quote" />
        <div className="home-content-field">
          <FilePicker
            accept=".png,.jpg,.jpeg"
            label="Choose Headshot"
            onSelect={(f) => setFile(f)}
          />
        </div>
        <PrismButton
          tone="primary"
          display="outline"
          disabled={!draftLeader.name}
          onPrismClick={async () => {
            await saveLeader(draftLeader, file || undefined);
            setDraftLeader({ name: "", title: "", quote: "", display_order: 0, active: true });
            setFile(null);
          }}
        >
          Add leader
        </PrismButton>
      </FieldGroup>
    </>
  );
};

const JoinUsEditor: React.FC<any> = ({ doc, setSection, uploadImage }) => {
  const joinUs = doc.joinUs || {};
  const messages = joinUs.messages || {};
  return (
    <>
      <FieldGroup title="Call to action" description="Controls which message and button appear in the Join Us section of the home page.">
        <PrismRadioGroup
          legend="Call-to-action mode"
          layout="stacked"
          value={joinUs.ctaMode === "override" ? "override" : "auto"}
          onPrismChange={(e: CustomEvent<{ value?: string }>) => {
            if (e.detail?.value === "override") {
              setSection("joinUs", { ctaMode: "override", ctaOverrideState: joinUs.ctaOverrideState || "apply" });
            } else {
              setSection("joinUs", { ctaMode: "auto" });
            }
          }}
        >
          <PrismRadio
            value="auto"
            label="Automatic (based on the application window's soft deadline)"
            checked={joinUs.ctaMode !== "override"}
          />
          <PrismRadio
            value="override"
            label="Manual override"
            checked={joinUs.ctaMode === "override"}
          />
        </PrismRadioGroup>
        {joinUs.ctaMode === "override" && (
          <div className="home-content-field">
            <label className="home-content-label" htmlFor="home-content-cta-override-state">Override state</label>
            <PrismSelect
              label="Override state"
              hideLabel
              value={joinUs.ctaOverrideState ? String(joinUs.ctaOverrideState) : ""}
              options={CTA_STATES}
              onPrismChange={(e: CustomEvent<{ value?: string }>) =>
                setSection("joinUs", { ctaOverrideState: e.detail?.value })
              }
            />
          </div>
        )}
      </FieldGroup>

      <FieldGroup title="Messages" description="The message shown for each call-to-action state. Only the active state appears live.">
        {CTA_STATES.map(({ value, editorLabel, help }) => (
          <div className="home-content-field" key={value}>
            <span className="home-content-label">{editorLabel}</span>
            <div className="home-content-field-help">{help}</div>
            {value === "fairNotif" && (
              <div className="home-content-annual-note">
                🗓️ <strong>Update every year:</strong> this message links to a specific year’s
                Project Fair. Refresh the link and any dates at the start of each program year.
              </div>
            )}
            <MessageEditor value={messages[value] || ""} onChange={(v: string) => setSection("joinUs", { messages: { ...messages, [value]: v } })} />
          </div>
        ))}
      </FieldGroup>

      <FieldGroup title="Contact & presentation" description="The contact email, section quote, and image shown with the Join Us section.">
        <PrismInputText
          label="Contact email"
          type="email"
          value={joinUs.contactEmail || ""}
          onPrismInput={(e: CustomEvent<{ value?: string }>) => setSection("joinUs", { contactEmail: e.detail?.value || "" })}
        />
        <PrismInputText
          label="Section quote"
          value={joinUs.quote || ""}
          onPrismInput={(e: CustomEvent<{ value?: string }>) => setSection("joinUs", { quote: e.detail?.value || "" })}
        />
        <ImageField label="Section image" section="apply" imageKey={joinUs.imageKey} onUpload={uploadImage} onChange={(k) => setSection("joinUs", { imageKey: k })} />
      </FieldGroup>
    </>
  );
};

export default HomeContent;
